/**
 * External dependencies
 */
import * as React from 'react';
import styled from 'styled-components';
import { useCallback } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import type { TextElement } from '@src/web-stories-wp/story-editor/src/elements/types';
import { NumericInput, PillGroup } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */

import { Color, Row } from '../../../form';
import { SimplePanel } from '../../panel';
import {
  DEFAULT_TEXT_BORDER,
  Space,
  TEXT_BORDER_ALIGNMENT_OPTIONS,
} from './shared';
import { MULTIPLE_DISPLAY_VALUE, MULTIPLE_VALUE } from '../../../../constants';
import elementIs from '@src/web-stories-wp/story-editor/src/elements/utils/elementIs';
import {
  focusStyle,
  inputContainerStyleOverride,
  useCommonObjectValue,
} from '../../shared';
import { TextBorderSettings } from './textBorderSettings';
import { TextBorderStrokeStyleSettings } from './textBorderStrokeStyle';
import { TextBorderStrokeLineJoinSettings } from './textBorderStrokeLineJoin';

const PillRow = styled(Row)`
  align-items: flex-start;
  justify-content: space-between;
  button {
    flex: 1;
    z-index: 1;
  }
`;

type TextBorderStylePanelProps = {
  selectedElements: TextElement[];
  pushUpdate: (
    value: Partial<TextElement> | ((element: TextElement) => TextElement),
    commitValues: boolean
  ) => void;
};

function TextBorderStylePanel(props: TextBorderStylePanelProps) {
  const { selectedElements, pushUpdate } = props;
  const textBorder = useCommonObjectValue(
    selectedElements,
    'textBorder',
    DEFAULT_TEXT_BORDER
  );

  const { alignment, color, lineCap, lineJoin, miterLimit, style, width } =
    textBorder;

  const allSupportTextBorder = selectedElements.every((el) =>
    elementIs.text(el)
  );

  const handleChange = useCallback(
    (name, value) =>
      pushUpdate(
        (element) => ({
          ...element,
          // SVG text doesn't support text-align: justify
          ...(name === 'width' && value > 0 && element.textAlign === 'justify'
            ? {
                textAlign: 'left',
              }
            : {}),
          textBorder: {
            ...DEFAULT_TEXT_BORDER,
            ...element.textBorder,
            [name]: value,
          },
        }),
        true
      ),
    [pushUpdate]
  );

  if (!allSupportTextBorder) {
    return null;
  }

  const hasTextBorder = width > 0;

  return (
    <SimplePanel
      name="textBorderStyle"
      title={__('Text Stroke', 'web-stories')}
    >
      <Row>
        <NumericInput
          aria-label={__('Border width', 'web-stories')}
          canBeNegative={false}
          min={0}
          isFloat
          value={width}
          onChange={(event, value) => {
            handleChange('width', value);
          }}
          onBlur={(event, value) => {
            handleChange('width', value);
          }}
          isIndeterminate={MULTIPLE_VALUE === width}
          placeholder={MULTIPLE_VALUE === width ? MULTIPLE_DISPLAY_VALUE : null}
          suffix={__('Width', 'web-stories')}
          containerStyleOverride={inputContainerStyleOverride}
        />

        <Space />
        <TextBorderSettings>
          <TextBorderStrokeLineJoinSettings
            lineJoin={lineJoin}
            miterLimit={miterLimit}
            handleChange={(property, value) => handleChange(property, value)}
          />
          <TextBorderStrokeStyleSettings
            lineCap={lineCap}
            style={style}
            handleChange={(property, value) => handleChange(property, value)}
          />
        </TextBorderSettings>
      </Row>
      {hasTextBorder && (
        <>
          <PillRow>
            <PillGroup
              onSelect={(value) => {
                handleChange('alignment', value);
              }}
              options={TEXT_BORDER_ALIGNMENT_OPTIONS}
              pillStyleOverride={focusStyle}
              value={alignment}
            />
          </PillRow>
          <Row>
            <Color
              allowsGradient
              allowsOpacity
              value={color}
              onChange={(value) => {
                handleChange('color', value);
              }}
              label={__('Text border color', 'web-stories')}
              changedStyle="text-border-color"
            />
          </Row>
        </>
      )}
    </SimplePanel>
  );
}

export default TextBorderStylePanel;
